import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject, zip } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { UserApiService } from 'src/app/modules/management/store/users';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import * as fromSecurityGroup from 'src/app/modules/management/store/security-groups';
import { CommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import { ChannelType, MassRecipientType, RecipientType } from 'src/app/modules/communications/store/communications/communications.model';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
    selector: 'topo-comms-recipient-inline-select',
    templateUrl: './recipient-inline-select.component.html',
    styleUrls: ['./recipient-inline-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: RecipientInlineSelectComponent
        }
    ],
    standalone: false
})
export class RecipientInlineSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder: string;
  @Input() set selectedMembers(value: any[]) {
    this._selectedMembers = value;
    this.members = [];
    this.addCustomMembers();
  }
  _selectedMembers: any[];
  get selectedMembers(): any[] {
    return this._selectedMembers;
  }
  @Input() channelTypes: ChannelType[];
  @Input() autoClear: boolean;
  @Input() includeUsers: boolean;
  @Input() includeGroups: boolean;
  @Input() includeDistro: boolean;
  @Output() selectedMembersChanged = new EventEmitter<any[]>();

  @ViewChild(NgSelectComponent) ngSelectComponent: NgSelectComponent;

  membersInput$ = new Subject<string>();
  destroy$: Subject<void> = new Subject<void>();
  users$ = new BehaviorSubject<any[]>([]);
  groups$ = new BehaviorSubject<any[]>([]);
  dists$ = new BehaviorSubject<any[]>([]);
  groupGroupName: string;
  userGroupName: string;
  distListGroupName: string;
  customGroupName: string;
  membersLoading: boolean;
  members: any[];
  typeToSearchText: string;
  MassRecipientType = MassRecipientType;

  touched = false;
  @Input() disabled = false;
  onChange = value => {};
  onTouched = () => {};

  constructor(
    private store: Store<fromAuth.State>,
    private translateService: TranslateService,
    private userApiService: UserApiService,
    private communicationsApiService: CommunicationsApiService,
    private securityGroupApi: fromSecurityGroup.SecurityGroupApiService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.groupGroupName = this.translateService.instant('COMMUNICATIONS.RECIPIENT-TYPE-GROUPS');
    this.distListGroupName = this.translateService.instant('COMMUNICATIONS.RECIPIENT-TYPE-DIST-LIST');
    this.userGroupName = this.translateService.instant('COMMUNICATIONS.RECIPIENT-TYPE-USERS');
    this.customGroupName = this.translateService.instant('COMMUNICATIONS.RECIPIENT-TYPE-CUSTOM');
    this.typeToSearchText = this.translateService.instant('UTILITY.NG-SELECT-PROMPT');

    zip(this.users$, this.groups$, this.dists$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(([users, groups, dists]) => {
        this.membersLoading = false;

        const values = [];

        if (!!users) {
          values.push(
            ...users?.map(u => {
              return {
                uId: u.uId,
                displayName: u.displayName,
                type: this.userGroupName,
                profilePhotoUrl: u.profilePhotoUrl,
                memberType: MassRecipientType.User
              };
            })
          );
        }

        if (!!groups) {
          values.push(
            ...groups?.map(d => {
              return {
                uId: d.uId,
                displayName: d.displayName,
                type: this.groupGroupName,
                memberType: MassRecipientType.Group
              };
            })
          );
        }

        if (!!dists) {
          values.push(
            ...dists?.map(d => {
              return {
                uId: d.uId,
                displayName: d.name,
                type: this.distListGroupName,
                memberType: MassRecipientType.DistributionList
              };
            })
          );
        }

        this.members = values;

        this.addCustomMembers();
      });

    this.membersInput$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(val => {
      this.searchForUsersAndGroups(val);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private addCustomMembers(): void {
    this._selectedMembers?.filter(m => m.memberType === MassRecipientType.Custom).forEach(m => {
      this.members.push({
        uId: m.uId,
        displayName: m.displayName,
        type: this.customGroupName,
        profilePhotoUrl: null,
        memberType: MassRecipientType.Custom
      });
    })
  }

  searchForUsersAndGroups(searchTerm: string): void {
    if (this.includeUsers === true) {
      this.getUsers(searchTerm)
        .pipe(take(1))
        .subscribe(users => {
          this.users$.next(users);
          this.changeDetector.markForCheck();
        });
    } else {
      this.users$.next([]);
    }

    if (this.includeGroups === true) {
      this.getGroups(searchTerm)
        .pipe(take(1))
        .subscribe(group => {
          this.groups$.next(group);
          this.changeDetector.markForCheck();
        });
    } else {
      this.groups$.next([]);
    }

    if (this.includeDistro === true) {
      this.getDistributionLists(searchTerm)
        .pipe(take(1))
        .subscribe(dist => {
          this.dists$.next(dist);
          this.changeDetector.markForCheck();
        });
    } else {
      this.dists$.next([]);
    }
  }

  getUsers(searchTerm: string = null): Observable<any[]> {
    this.membersLoading = true;

    return this.store.select(fromAuth.selectUserContext).pipe(
      take(1),
      switchMap(userContext => {
        return this.userApiService
          .getUsers(userContext.organizationUId, {
            skip: 0,
            take: 20,
            sort: 'DisplayName',
            where: !!searchTerm
              ? `DisplayName.ToLower().Contains("${searchTerm.replace('"', '\\"')?.toLocaleLowerCase()}") AND IsServiceAccount==false`
              : 'IsServiceAccount==false'
          })
          .pipe(
            catchError(() => of({ data: [] })),
            map(result => {
              return result?.data;
            })
          );
      })
    );
  }

  getGroups(searchTerm: string = null): Observable<any[]> {
    this.membersLoading = true;

    return this.store.select(fromAuth.selectUserContext).pipe(
      take(1),
      switchMap(userContext =>
        this.securityGroupApi
          .getSecurityGroups(userContext.organizationUId, {
            skip: 0,
            take: 100,
            sort: 'DisplayName',
            where: !!searchTerm ? `DisplayName.ToLower().Contains("${searchTerm.replace('"', '"').toLocaleLowerCase()}")` : ''
          })
          .pipe(
            catchError(() => of({ data: [] })),
            map(result => {
              return result?.data;
            })
          )
      )
    );
  }

  getDistributionLists(searchTerm: string = null): Observable<any[]> {
    this.membersLoading = true;

    let where = !!searchTerm ? `Name.ToLower().Contains("${searchTerm.replace('"', '\\"')?.toLocaleLowerCase()}")` : '';

    return this.communicationsApiService
      .getDistributionLists({
        skip: 0,
        take: 20,
        sort: 'Name',
        where
      })
      .pipe(
        catchError(e => {
          console.error(e);
          return of({ data: [] });
        }),
        map(result => {
          return result?.data;
        })
      );
  }

  memberChanged(event): void {
  }

  addMember(event): void {
    this.selectedMembersChanged.emit(this.selectedMembers);
    this.markAsTouched();
    this.onChange(this.selectedMembers);
    this.membersLoading = false;
  }

  removeMember(event): void {
    this.selectedMembersChanged.emit(this.selectedMembers);
    this.markAsTouched();
    this.onChange(this.selectedMembers);
    this.membersLoading = false;
  }

  writeValue(selectedMembers: any[]) {
    this.selectedMembers = selectedMembers;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  trackByFn(val) {
    return val;
  }
}
